import GetAuthContext from "../../../../src/shared/context/AuthContext";
import type { Tab } from "../../components/atomic/Tabs/interface";
import { LEAD_DETAILS_TABS } from "../../services/Leads/constants";
import { LOAN_APPLICATION_DETAILS_TABS } from "../../services/LoanApplication/constants";

import type { FinBoxResponse } from "../services/APIResponse";

import {
  getLenderUserInfoFromLocalStorage,
  getPermissionsFromStorage,
  getUserInfoFromLocalStorage
} from "../services/Fetch";

import {
  EMAIL_LABEL,
  NO_HEADERS_RESPONSE,
  ORG_IDS,
  PAGES_LIST,
  LENDER_IDs
} from "./constants";

import { DOCUMENT_IDS } from "./constants";

import { AccessMode, Source } from "../../core/access-control/types";
import type { RenderStrategy } from "../../core/access-control/types";

import { notify } from "../../../../src/shared/context/AlertContext";

export function getSearchParamsFromUrl<
  T extends { name: string; value: string }
>(url: string, typesList: Array<T>) {
  const pageUrl = new URL(url);
  const searchParams = Object.fromEntries(pageUrl.searchParams);
  let searchType = "";
  let searchQuery = "";
  typesList?.some((type) => {
    if (searchParams?.[type.value] && type.value !== "workflowTypes") {
      searchType = type.value;
      searchQuery = searchParams[searchType];
      return true;
    } else {
      return false;
    }
  });

  return {
    searchType,
    searchQuery
  };
}

export function getRedirectionUrl(lenderID: string, isHome: boolean = false) {
  switch (lenderID) {
    case LENDER_IDs.MFL_LAP:
    case LENDER_IDs.ABFL_CO_LENDING:
    case LENDER_IDs.HFCL:
    case LENDER_IDs.TVSCS:
      return "/my-queue";
    case LENDER_IDs.MFL:
      if (isHome) return "/loan-applications";
      else return "/my-queue";
    default:
      return "/loan-applications";
  }
}

export function formatNumberInput(value: string | number): string {
  if (value === null || value === undefined || value === "") return "";
  const numeric = value.toString().replace(/,/g, "");
  return Number(numeric).toLocaleString("en-IN");
}

export function roundToDecimal(
  value: number | string,
  decimalPlaces = 2
): number | string {
  if (!isNaN(Number(value))) {
    return Number.isInteger(Number(value))
      ? Number(value)
      : Number(value).toFixed(decimalPlaces);
  }
  return value;
}

export function getFormattedCurrency(number: number) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0
  }).format(number);
}

export function getFormattedNumber(number: number) {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0
  }).format(number);
}

export function getLoanApplicationDetailsUrl(
  loanApplicationID: string,
  isLenderDashboard: boolean,
  sourceEntityID?: string,
  organizationID?: string,
  lenderID?: string,
  userID?: string
): string {
  // For Platform Dashboard - check organizationID and include sourceEntityID
  if (!isLenderDashboard && typeof isLenderDashboard === "boolean") {
    const isMflLap =
      organizationID !== undefined && organizationID === ORG_IDS.MFL_LAP;

    const isPflUser =
      organizationID !== undefined && organizationID === ORG_IDS.PFL_EL;

    const url = `/loan-applications/details/${sourceEntityID}/${loanApplicationID}`;

    return isPflUser
      ? `/loan-applications/details/${
          userID ? userID : sourceEntityID
        }/${loanApplicationID}`
      : url;
  }

  return `/loan-applications/details/${loanApplicationID}`;
}

export const blobCreationFromURI = (dataurl: any) => {
  let arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  delay: number
) => {
  let debounceTimer: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func(...args), delay);
  };
};

export const CREDITLINE_TABS = [
  "Timeline",
  "Details",
  "Case Status",
  "Application Form",
  "CreditLine Details",
  "KYC",
  "CreditLine Transactions",
  "Bank Info",
  "Partner Details",
  "Payments",
  "Bank Statements",
  "Additional Docs",
  "Notes"
] as const;

export const PERSONAL_LOAN_TABS = [
  "Timeline",
  "Workflow Timeline",
  "Details",
  "Loan Term Deviation",
  "Case Status",
  "Application Form",
  "KYC",
  "Loan Offer",
  "Bank Info",
  "Partner Details",
  "Payments",
  "Bank Statements",
  "Additional Docs",
  "Deviation",
  "Notes"
] as const;

export const TABS_MAP = {
  loan_application: {
    Details: PAGES_LIST.LOAN_APPLICATION_DETAILS_TAB,
    "Case Status": PAGES_LIST.LOAN_APPLICATION_CASE_STATUS_TAB,
    Timeline: PAGES_LIST.LOAN_APPLICATION_TIMELINE_TAB,
    "Workflow Timeline": PAGES_LIST.LOAN_APPLICATION_WORKFLOW_TIMELINE_TAB,
    Deviation: PAGES_LIST.LOAN_APPLICATION_DEVIATION_TAB,
    "CreditLine Details": PAGES_LIST.LOAN_APPLICATION_CREDITLINE_DETAILS_TAB,
    "CreditLine Transactions":
      PAGES_LIST.LOAN_APPLICATION_CREDITLINE_TRANSACTIONS_TAB,
    "Application Form": PAGES_LIST.LOAN_APPLICATION_FORM_TAB,
    KYC: PAGES_LIST.LOAN_APPLICATION_KYC_TAB,
    "Co-Applicants": PAGES_LIST.CO_APPLICANT_DETAILS_PAGE,
    Directors: PAGES_LIST.LOAN_APPLICATION_DIRECTOR_TAB,
    "Loan Offer": PAGES_LIST.LOAN_APPLICATION_LOAN_OFFER_TAB,
    "Bank Info": PAGES_LIST.LOAN_APPLICATION_BANK_INFO_TAB,
    "Partner Details": PAGES_LIST.LOAN_APPLICATION_PARTNER_INFO_TAB,
    Payments: PAGES_LIST.LOAN_APPLICATION_PAYMENTS_TAB,
    "Business Details": PAGES_LIST.LOAN_APPLICATION_BUSINESS_DETAILS_TAB,
    "Bank Statements": PAGES_LIST.LOAN_APPLICATION_BANK_STATEMENTS_TAB,
    "Additional Docs": PAGES_LIST.LOAN_APPLICATION_ADDITIONAL_DOCS_TAB,
    Notes: PAGES_LIST.LOAN_APPLICATION_NOTES_TAB,
    "Loan Term Deviation": PAGES_LIST.LOAN_APPLICATION_LOAN_TERM_DEVIATION_TAB
  },
  leads: {
    Details: PAGES_LIST.LOAN_APPLICATION_DETAILS_TAB,
    "Case Status": PAGES_LIST.LOAN_APPLICATION_CASE_STATUS_TAB,
    Timeline: PAGES_LIST.LOAN_APPLICATION_TIMELINE_TAB,
    "Partner Details": PAGES_LIST.LOAN_APPLICATION_PARTNER_INFO_TAB,
    "Business Details": PAGES_LIST.LOAN_APPLICATION_BUSINESS_DETAILS_TAB,
    "Bank Statements": PAGES_LIST.LOAN_APPLICATION_BANK_STATEMENTS_TAB
  }
} as const;

interface CustomTabs {
  CREDITLINE_TABS?: Tab[];
  BUSINESS_LOAN_TABS?: Tab[];
  LOAN_TABS?: Tab[];
  [key: string]: Tab[] | undefined;
}

export const getTabsByProductType = (
  entityType: "loan_application" | "leads",
  productType: "credit_line" | "business_loan" | "personal_loan" | "overdraft",
  permissions: Record<string, unknown> = {},
  customTabs: CustomTabs
) => {
  let filteredTabs: Array<Tab> = [];

  if (entityType === "leads") {
    filteredTabs = LEAD_DETAILS_TABS;
  } else {
    if (productType === "credit_line") {
      filteredTabs = customTabs["CREDITLINE_TABS"] || [];
    } else if (productType === "overdraft") {
      filteredTabs = customTabs["CREDITLINE_TABS"] || [];
    } else if (productType === "business_loan") {
      filteredTabs = customTabs["BUSINESS_LOAN_TABS"] || [];
    } else if (productType === "personal_loan") {
      filteredTabs = customTabs["LOAN_TABS"] || [];
    }
    filteredTabs = filteredTabs.map(
      (tab) => LOAN_APPLICATION_DETAILS_TABS[tab.name]
    );
  }

  filteredTabs = filteredTabs.filter((tab) => {
    if (tab) {
      const readableName = TABS_MAP?.[entityType]?.[tab.name];
      return !!permissions[readableName];
    }
  });

  return filteredTabs;
};

export const LENDER_IDs = {
  ABFLID: "c1a55e53-4701-49a1-923c-3cf32a66b5ec",
  IIFLID: "b46205ae-14f3-43ae-bedb-86d4d82df2f8",
  MUTHOOTID: "ad97435f-af0b-4bed-849e-1b309f943087",
  MFL: "8efd25fb-9c1f-4adc-90a8-7e2cc0b17775",
  ABFLPL: "da77e829-52c2-4b1f-9686-2d5bf04660ed",
  MFL_LAP: "0879bda2-39c7-4d6a-a55e-bb9f7b13389d",
  ABFL_CO_LENDING: "ebc32b84-1b56-4270-a390-9f384f095e5e",
  AXIS: "07d0b5dd-d098-48b2-beec-a99f7948c537",
  AXIS_NEW: "813e7c91-5a21-486d-83b8-abca0d724abb",
  HFCL: "6a5255c5-9fce-4654-8881-6c9c4efde9e8",
  HERO_FINCORP: "6a5255c5-9fce-4654-8881-6c9c4efde9e8",
  INDOSTAR: "0124e011-cee0-4ba0-8e03-35954fe92270",
  TVSCS: "17a82cb1-0195-4865-9220-48d6b539ca2a"
};

interface SearchType {
  value: string;
  [key: string]: unknown;
}

export function getFilteredSearchTypes(
  disallowedFields: string[],
  searchTypes: SearchType[]
) {
  return searchTypes.filter(
    (searchType) => !disallowedFields.includes(searchType.value)
  );
}

interface ObjectArrayItem {
  name: string;
  value: unknown;
}

function objectToArray(obj: Record<string, unknown>): ObjectArrayItem[] {
  const result: ObjectArrayItem[] = [];
  Object.keys(obj ?? {}).map((key) => {
    if (
      obj[key] !== null &&
      typeof obj[key] === "object" &&
      !Array.isArray(obj[key])
    ) {
      const valueArray = objectToArray(obj[key] as Record<string, unknown>);
      valueArray.map((valueEntry) => {
        result.push({
          name: (key !== "data" ? key + "." : "") + valueEntry.name,
          value: valueEntry.value
        });
      });
    } else if (Array.isArray(obj[key])) {
      result.push({
        name: key,
        value: JSON.stringify(obj[key])
      });
    } else {
      result.push({
        name: key,
        value: obj[key]
      });
    }
  });

  return result;
}

export function parseJsonToArray(json: Record<string, unknown>) {
  const { ...rest } = json ?? {};
  let rows: ObjectArrayItem[] = [];

  if (rest && Object.keys(rest)?.length > 0) {
    rows = [...rows, ...objectToArray(rest)];
  }

  const labels: Record<string, unknown> = {};

  if (rows && rows?.length > 0) {
    rows.map((row) => {
      labels[row.name] = row.name;
    });
  }

  return { labels, rows };
}

export const updateRetriggerList = (id: string) => {
  let retriggerList = localStorage.getItem("retriggerList");
  if (retriggerList) {
    retriggerList = JSON.parse(retriggerList);
  }

  if (!retriggerList?.list?.find((item: string) => item === id)) {
    retriggerList = {
      list: []
    };
    retriggerList?.["list"].push(id);

    localStorage.setItem("retriggerList", JSON.stringify(retriggerList));
  }
};

export const removeRetriggerItem = (id: string) => {
  let retriggerList = localStorage.getItem("retriggerList");
  retriggerList = JSON.parse(retriggerList || "{}");
  const index = retriggerList?.["list"].indexOf(id);

  if (index > -1) {
    retriggerList?.["list"].splice(index, 1);
    localStorage.setItem("retriggerList", JSON.stringify(retriggerList));
  }
};

export const insertAtIndex = <T>(
  array: T[],
  insertIndex: number,
  itemToInsert: T
): T[] => {
  const newArray = [...array];
  newArray.splice(insertIndex, 0, itemToInsert);
  return newArray;
};

export const isValidDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
};

export async function noHeadersResponse(): Promise<FinBoxResponse> {
  console.log("No header response");

  return NO_HEADERS_RESPONSE;
}

interface UserData {
  userData?: {
    rbacGroupName?: string;
  };
  [key: string]: unknown;
}

export const getUserRbacGroup = (): string => {
  const user: string | null = window.localStorage.getItem(
    "finbox-lender-dashboard-user"
  );

  if (user) {
    try {
      const parsedUser: UserData = JSON.parse(user);
      return parsedUser?.userData?.rbacGroupName || "";
    } catch (error) {
      console.error("Error parsing user data", error);
    }
  }
  return "";
};

export function checkPermission(
  pageName: string,
  sourceEntityID?: string,
  functionalAPI?: string
): boolean {
  const { userState } = GetAuthContext();
  const { rbacPermissions = {} } = userState || {};

  let isAllowed = Object.keys(rbacPermissions || {}).includes(pageName);
  if (sourceEntityID) {
    isAllowed =
      isAllowed && Object.hasOwn(rbacPermissions[pageName], sourceEntityID);
    if (functionalAPI) {
      isAllowed =
        isAllowed &&
        rbacPermissions[pageName][sourceEntityID].includes(functionalAPI);
    }
  }
  return isAllowed;
}

interface Permissions {
  [pageName: string]: {
    [key: string]: string[];
  };
}

export function checkIfActionDisallowed({
  pageName,
  action
}: {
  pageName: string;
  action: string;
}): boolean {
  const permissions: Permissions = getPermissionsFromStorage();

  return Object.values(permissions[pageName] || {}).some(
    (actionList: string[]) => actionList.includes(action)
  );
}

export const forceDownloadLink = async (
  url: string,
  fileName: string
): Promise<void> => {
  let objURL: string | null = null;
  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch: ${response.status} ${response.statusText}`
      );
    }

    const blob = await response.blob();
    objURL = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = objURL;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Small delay before revoking to ensure download starts
    setTimeout(() => {
      if (objURL) window.URL.revokeObjectURL(objURL);
    }, 100);
  } catch (error) {
    // Clean up object URL if it was created
    if (objURL) window.URL.revokeObjectURL(objURL);

    throw error; // Re-throw so caller can handle it
  }
};

type SsoConfig = {
  provider: "keycloak" | "generic";
  method: "alternate" | "enforced";
  emailLabel?:
    | typeof EMAIL_LABEL.EMPLOYEE_ID
    | typeof EMAIL_LABEL.EMAIL_ADDRESS;
};

export type OrganizationConfig = {
  isDSM?: boolean;
  metadata?: { programType: string };
  appID: "platform_dashboard" | "lending_dashboard";
  organizationID?: string;
  lenderID?: string;
  ssoConfig?: SsoConfig;
  ssoEmailDomains?: string[];
  renderStrategy?: RenderStrategy;
};

export const MULTI_DOMAIN_CONFIG: { [domain: string]: OrganizationConfig } = {
  "mfl-lap-platform.finbox.in": {
    isDSM: true,
    appID: "platform_dashboard",
    organizationID: "2a00611d-fbe6-484b-a6ef-670769ceeea3",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate",
      emailLabel: EMAIL_LABEL.EMPLOYEE_ID
    }
  },
  "mfl-lap-lenders.finbox.in": {
    appID: "lending_dashboard",
    lenderID: "0879bda2-39c7-4d6a-a55e-bb9f7b13389d",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate",
      emailLabel: EMAIL_LABEL.EMPLOYEE_ID
    }
  },
  "adityabirla-colending-lenders.finbox.in": {
    appID: "lending_dashboard",
    lenderID: "ebc32b84-1b56-4270-a390-9f384f095e5e",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate",
      emailLabel: EMAIL_LABEL.EMPLOYEE_ID
    }
  },
  "mfl-platform.finbox.in": {
    appID: "platform_dashboard",
    organizationID: "148fa0b6-f63a-4938-ac69-79831e54200f",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardSidebar: Source.UiConfig,
      dashboardTabContent: Source.Default
    }
  },
  "mfl-lenders.finbox.in": {
    appID: "lending_dashboard",
    lenderID: LENDER_IDs.MFL,
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardSidebar: Source.UiConfig,
      dashboardTabContent: Source.Default
    }
  },
  "adityabirla-platform.finbox.in": {
    appID: "platform_dashboard",
    organizationID: "eabdf414-d3f3-4f1a-ad71-f01d59d9c05b",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: ["@adityabirlacapital.com"]
  },
  "adityabirla-bl-lenders.finbox.in": {
    appID: "lending_dashboard",
    lenderID: LENDER_IDs.ABFLID,
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: ["@adityabirlacapital.com"]
  },
  "adityabirla-pl-platform.finbox.in": {
    appID: "platform_dashboard",
    organizationID: "65c5927f-5673-440e-a7be-e0b51e42c103",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: ["@adityabirlacapital.com"]
  },
  "adityabirla-pl-lenders.finbox.in": {
    appID: "lending_dashboard",
    lenderID: LENDER_IDs.ABFLPL,
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: ["@adityabirlacapital.com"]
  },
  "platform.finbox.in": {
    appID: "platform_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: ["@poonawallafincorp.com"]
  },
  "poonawallafincorp-platform.finbox.in": {
    uiConfigNavbar: true,
    metadata: {
      programType: "BUSINESS_LOAN"
    },
    appID: "platform_dashboard",
    organizationID: ORG_IDS.PFL_EL,
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: ["@poonawallafincorp.com", "@gmail.com", "@idp.com"]
  },
  "poonawallafincorp-lenders.finbox.in": {
    appID: "lending_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: ["@poonawallafincorp.com", "@gmail.com", "@idp.com"]
  },
  "platformuat.finbox.in": {
    organizationID: ORG_IDS.PFL_EL,
    metadata: {
      programType: "BUSINESS_LOAN"
    },
    appID: "platform_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: [
      "@poonawallafincorp.com",
      "@ssouser.com",
      "@gmail.com",
      "@idp.com"
    ],
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardSidebar: Source.Default,
      dashboardTabContent: Source.Default
    }
  },
  "finbox-platform-dashboard-uat-1.s3-website.ap-south-1.amazonaws.com": {
    uiConfigNavbar: true,
    byPassRbac: true,
    organizationID: ORG_IDS.PFL_EL,
    metadata: {
      programType: "BUSINESS_LOAN"
    },
    appID: "platform_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: [
      "@poonawallafincorp.com",
      "@ssouser.com",
      "@gmail.com",
      "@idp.com"
    ]
  },
  "pfl-platform.fbxcloud.in": {
    uiConfigNavbar: true,
    byPassRbac: true,
    organizationID: ORG_IDS.PFL_EL,
    metadata: {
      programType: "BUSINESS_LOAN"
    },
    appID: "platform_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: [
      "@poonawallafincorp.com",
      "@ssouser.com",
      "@gmail.com",
      "@idp.com"
    ]
  },
  "finbox-platform-dashboard-uat-29.s3-website.ap-south-1.amazonaws.com": {
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardTabContent: Source.Default
    },
    organizationID: ORG_IDS.PFL_NEW,
    metadata: {
      programType: "BUSINESS_LOAN"
    },
    appID: "platform_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: [
      "@poonawallafincorp.com",
      "@ssouser.com",
      "@gmail.com",
      "@idp.com"
    ]
  },
  "tvs-platform.fbxcloud.in": {
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardTabContent: Source.Default
    },
    organizationID: ORG_IDS.PFL_NEW,
    metadata: {
      programType: "BUSINESS_LOAN"
    },
    appID: "platform_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: [
      "@poonawallafincorp.com",
      "@ssouser.com",
      "@gmail.com",
      "@idp.com"
    ]
  },
  "finbox-platform-dashboard-uat-1.s3-website.ap-south-1.amazonaws.com": {
    organizationID: ORG_IDS.PFL_EL,
    metadata: {
      programType: "BUSINESS_LOAN"
    },
    appID: "platform_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: [
      "@poonawallafincorp.com",
      "@ssouser.com",
      "@gmail.com",
      "@idp.com"
    ]
  },
  "pfl-platform.fbxcloud.in": {
    organizationID: ORG_IDS.PFL_EL,
    metadata: {
      programType: "BUSINESS_LOAN"
    },
    appID: "platform_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: [
      "@poonawallafincorp.com",
      "@ssouser.com",
      "@gmail.com",
      "@idp.com"
    ]
  },
  "finbox-platform-dashboard-uat-12.s3-website.ap-south-1.amazonaws.com": {
    appID: "platform_dashboard",
    organizationID: "eabdf414-d3f3-4f1a-ad71-f01d59d9c05b"
  },
  "mfo-middleware-platform.fbxcloud.in": {
    appID: "platform_dashboard",
    organizationID: "eabdf414-d3f3-4f1a-ad71-f01d59d9c05b"
  },
  "finbox-platform-dashboard-uat-10.s3-website.ap-south-1.amazonaws.com": {
    appID: "platform_dashboard",
    organizationID: "eabdf414-d3f3-4f1a-ad71-f01d59d9c05b",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    }
  },
  "adityabirla-bl-platform.fbxcloud.in": {
    appID: "platform_dashboard",
    organizationID: "eabdf414-d3f3-4f1a-ad71-f01d59d9c05b",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    }
  },
  "platform-dashboard-non-prod.s3-website.ap-south-1.amazonaws.com": {
    appID: "platform_dashboard",
    organizationID: "eabdf414-d3f3-4f1a-ad71-f01d59d9c05b",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    }
  },
  "finbox-platform-dashboard-dev-6.s3-website.ap-south-1.amazonaws.com": {
    appID: "platform_dashboard",
    organizationID: "eabdf414-d3f3-4f1a-ad71-f01d59d9c05b"
  },
  "finbox-lending-platform-dev-2.s3-website.ap-south-1.amazonaws.com": {
    appID: "platform_dashboard",
    organizationID: "65c5927f-5673-440e-a7be-e0b51e42c103"
  },
  "finbox-lender-dashboard-uat-10.s3-website.ap-south-1.amazonaws.com": {
    appID: "lending_dashboard",
    lenderID: LENDER_IDs.ABFLID,
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    }
  },
  "adityabirla-bl-lender.fbxcloud.in": {
    appID: "lending_dashboard",
    lenderID: LENDER_IDs.ABFLID,
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    }
  },
  "finbox-platform-dashboard-uat21.s3-website.ap-south-1.amazonaws.com": {
    isDSM: true,
    appID: "platform_dashboard",
    organizationID: "2a00611d-fbe6-484b-a6ef-670769ceeea3",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    }
  },
  "mfl-lap-platform.fbxcloud.in": {
    isDSM: true,
    appID: "platform_dashboard",
    organizationID: "2a00611d-fbe6-484b-a6ef-670769ceeea3",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    }
  },
  "finbox-lender-dashboard-uat21.s3-website.ap-south-1.amazonaws.com": {
    appID: "lending_dashboard",
    lenderID: "0879bda2-39c7-4d6a-a55e-bb9f7b13389d",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    }
  },
  "mfl-lap-lender.fbxcloud.in": {
    appID: "lending_dashboard",
    lenderID: "0879bda2-39c7-4d6a-a55e-bb9f7b13389d",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    }
  },
  "finbox-lender-dashboard-uat-3.s3-website.ap-south-1.amazonaws.com": {
    appID: "lending_dashboard",
    uiConfigNavbar: true,
    byPassRbac: true,
    lenderID: LENDER_IDs.MFL,
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardTabContent: Source.Default
    }
  },
  "mfl-bl-lender.fbxcloud.in": {
    appID: "lending_dashboard",
    uiConfigNavbar: true,
    byPassRbac: true,
    lenderID: LENDER_IDs.MFL,
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardTabContent: Source.Default
    }
  },
  "platform-uat-21.finbox.in": {
    appID: "platform_dashboard",
    organizationID: "2a00611d-fbe6-484b-a6ef-670769ceeea3",
    ssoConfig: {
      provider: "keycloak",
      emailLabel: EMAIL_LABEL.EMPLOYEE_ID,
      method: "alternate"
    }
  },
  "lenders-uat-21.finbox.in": {
    appID: "lending_dashboard",
    lenderID: "0879bda2-39c7-4d6a-a55e-bb9f7b13389d",
    ssoConfig: {
      provider: "keycloak",
      emailLabel: EMAIL_LABEL.EMPLOYEE_ID,
      method: "alternate"
    }
  },
  "finbox-lender-dashboard-uat-25.s3-website.ap-south-1.amazonaws.com": {
    appID: "lending_dashboard",
    lenderID: "0879bda2-39c7-4d6a-a55e-bb9f7b13389d",
    ssoConfig: {
      provider: "keycloak",
      emailLabel: EMAIL_LABEL.EMPLOYEE_ID,
      method: "alternate"
    }
  },
  "finbox-lender-dashboard-uat-4.s3-website.ap-south-1.amazonaws.com": {
    appID: "lending_dashboard",
    lenderID: "ebc32b84-1b56-4270-a390-9f384f095e5e",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardTabContent: Source.UiConfig
    }
  },
  "finbox-platform-dashboard-uat-3.s3-website.ap-south-1.amazonaws.com": {
    appID: "platform_dashboard",
    organizationID: "148fa0b6-f63a-4938-ac69-79831e54200f",
    uiConfigNavbar: true,
    byPassRbac: true,
    ssoConfig: {
      provider: "generic",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardTabContent: Source.Default
    }
  },
  "mfl-bl-platform.fbxcloud.in": {
    appID: "platform_dashboard",
    organizationID: "148fa0b6-f63a-4938-ac69-79831e54200f",
    uiConfigNavbar: true,
    byPassRbac: true,
    ssoConfig: {
      provider: "generic",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardTabContent: Source.Default
    }
  },
  "finbox-platform-dashboard-uat-8.s3-website.ap-south-1.amazonaws.com": {
    appID: "platform_dashboard",
    organizationID: ORG_IDS.HDFC_PL,
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: ["@hbctxdom.com", "@hdfcbankuat.onmicrosoft.com"]
  },
  "hdfc-platform.fbxcloud.in": {
    appID: "platform_dashboard",
    organizationID: ORG_IDS.HDFC_PL,
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: ["@hbctxdom.com", "@hdfcbankuat.onmicrosoft.com"]
  },
  "lending-platform-uat3.finbox.in": {
    appID: "platform_dashboard",
    organizationID: "148fa0b6-f63a-4938-ac69-79831e54200f",
    ssoConfig: {
      provider: "generic",
      method: "alternate"
    },
    renderStrategy: { dashboardTabs: Source.UiConfig }
  },
  "finbox-platform-dashboard-uat20.s3-website.ap-south-1.amazonaws.com": {
    appID: "platform_dashboard",
    organizationID: "",
    ssoConfig: {
      provider: "generic",
      method: "alternate"
    }
  },
  "finbox-lender-dashboard-uat-6.s3-website.ap-south-1.amazonaws.com": {
    appID: "lending_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    }
  },
  "mfo-lender.fbxcloud.in": {
    appID: "lending_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    }
  },
  "adityabirla-pl-lender.fbxcloud.in": {
    appID: "lending_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    }
  },
  "finbox-platform-dashboard-uat-6.s3-website.ap-south-1.amazonaws.com": {
    appID: "platform_dashboard",
    organizationID: "65c5927f-5673-440e-a7be-e0b51e42c103",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    }
  },
  "mfo-platform.fbxcloud.in": {
    appID: "platform_dashboard",
    organizationID: "65c5927f-5673-440e-a7be-e0b51e42c103",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    }
  },
  "adityabirla-pl-platform.fbxcloud.in": {
    appID: "platform_dashboard",
    organizationID: "65c5927f-5673-440e-a7be-e0b51e42c103",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    }
  },
  "finbox-platform-dashboard-uat-20.s3-website.ap-south-1.amazonaws.com": {
    appID: "platform_dashboard",
    organizationID: "65c5927f-5673-440e-a7be-e0b51e42c103",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    }
  },
  "finbox-platform-dashboard-uat-25.s3-website.ap-south-1.amazonaws.com": {
    isDSM: true,
    appID: "platform_dashboard",
    organizationID: "2a00611d-fbe6-484b-a6ef-670769ceeea3",
    ssoConfig: {
      provider: "keycloak",
      emailLabel: EMAIL_LABEL.EMPLOYEE_ID,
      method: "alternate"
    }
  },
  "finbox-platform-dashboard-uat-5.s3-website.ap-south-1.amazonaws.com": {
    appID: "platform_dashboard",
    organizationID: "12824bf0-0ce8-4fbf-9528-0cf803da3d68",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    }
  },
  "finbox-platform-dashboard-uat-5-herofincorp.s3-website.ap-south-1.amazonaws.com":
    {
      appID: "platform_dashboard",
      organizationID: "427e18bc-34e8-450f-b7a6-20c974375939",
      ssoConfig: {
        provider: "keycloak",
        method: "enforced"
      },
      renderStrategy: {
        navbar: Source.UiConfig,
        dashboardTabs: Source.UiConfig,
        accessMode: AccessMode.UiConfig,
        dashboardTabContent: Source.Default
      }
    },
  "finbox-lender-dashboard-uat-5.s3-website.ap-south-1.amazonaws.com": {
    appID: "lending_dashboard",
    lenderID: "813e7c91-5a21-486d-83b8-abca0d724abb",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    },
    renderStrategy: { navbar: Source.Default },
    ssoEmailDomains: ["@axisbank.com", "axisfinance.in"]
  },
  "finbox-lender-dashboard-uat-19.s3-website.ap-south-1.amazonaws.com": {
    appID: "lending_dashboard",
    lenderID: LENDER_IDs.HFCL,
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardTabs: Source.UiConfig,
      dashboardSidebar: Source.UiConfig
    },
    ssoEmailDomains: ["@axisbank.com", "axisfinance.in"]
  },
  "herofincorp-lender.fbxcloud.in": {
    appID: "lending_dashboard",
    lenderID: LENDER_IDs.HFCL,
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardTabs: Source.UiConfig,
      dashboardSidebar: Source.UiConfig
    },
    ssoEmailDomains: ["@axisbank.com", "axisfinance.in"]
  },
  "finbox-platform-dashboard-uat-19.s3-website.ap-south-1.amazonaws.com": {
    appID: "platform_dashboard",
    organizationID: "427e18bc-34e8-450f-b7a6-20c974375939",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardSidebar: Source.UiConfig
    }
  },
  "herofincorp-platform.fbxcloud.in": {
    appID: "platform_dashboard",
    organizationID: "427e18bc-34e8-450f-b7a6-20c974375939",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardSidebar: Source.UiConfig
    }
  },
  "finbox-lender-dashboard-uat-27.s3-website.ap-south-1.amazonaws.com": {
    appID: "lending_dashboard",
    lenderID: "17a82cb1-0195-4865-9220-48d6b539ca2a",
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardSidebar: Source.UiConfig
    }
  },
  "finbox-platform-dashboard-uat-27.s3-website.ap-south-1.amazonaws.com": {
    appID: "platform_dashboard",
    organizationID: "b69631a3-36ee-4b80-8998-3ff78844f48d",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardSidebar: Source.UiConfig
    }
  },
  "finbox-mfl-lap.s3-website.ap-south-1.amazonaws.com": {
    appID: "lending_dashboard",
    lenderID: "0879bda2-39c7-4d6a-a55e-bb9f7b13389d",
    ssoConfig: {
      provider: "keycloak",
      emailLabel: EMAIL_LABEL.EMPLOYEE_ID,
      method: "alternate"
    }
  },
  "platformdashboard.hdfcef.prod.finbox.in": {
    appID: "platform_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: ["@hbctxdom.com", "@hdfcbankuat.onmicrosoft.com"]
  },
  "lenderdashboard.hdfcef.prod.finbox.in": {
    appID: "lending_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: ["@hbctxdom.com", "@hdfcbankuat.onmicrosoft.com"]
  },
  "herofincorp-lenders.finbox.in": {
    appID: "lending_dashboard",
    lenderID: LENDER_IDs.HFCL,
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardSidebar: Source.UiConfig
    }
  },
  "herofincorp-platform.finbox.in": {
    appID: "platform_dashboard",
    organizationID: ORG_IDS.HFCL,
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardSidebar: Source.UiConfig
    }
  },
  "tvscs-pl-lenders.finbox.in": {
    appID: "lending_dashboard",
    lenderID: LENDER_IDs.TVSCS,
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardSidebar: Source.UiConfig
    }
  },
  "tvscs-pl-platform.finbox.in": {
    appID: "platform_dashboard",
    organizationID: ORG_IDS.TVSCS,
    ssoConfig: {
      provider: "keycloak",
      method: "alternate"
    },
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardSidebar: Source.UiConfig
    }
  },
  "afl-pl-lenders.finbox.in": {
    appID: "lending_dashboard",
    ssoConfig: {
      provider: "keycloak",
      emailLabel: EMAIL_LABEL.EMAIL_ADDRESS,
      method: "alternate"
    }
  },
  "lenders-uat-5.finbox.in": {
    appID: "lending_dashboard",
    lenderID: "813e7c91-5a21-486d-83b8-abca0d724abb",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: ["@axisbank.com", "axisfinance.in"]
  },
  localhost: {
    isDSM: true,
    renderStrategy: {
      navbar: Source.UiConfig,
      dashboardTabs: Source.UiConfig,
      accessMode: AccessMode.UiConfig,
      dashboardSidebar: Source.Default,
      dashboardTabContent: Source.Default
    },
    lenderID: LENDER_IDs.MFL,
    organizationID: ORG_IDS.PFL_EL,
    appID: "platform_dashboard",
    ssoConfig: {
      provider: "keycloak",
      method: "enforced"
    },
    ssoEmailDomains: [
      "@poonawallafincorp.com",
      "@ssouser.com",
      "@gmail.com",
      "@idp.com"
    ]
  }
};

export function getOrganizationConfigByDomain(): OrganizationConfig {
  const hostname = window.location.hostname as keyof typeof MULTI_DOMAIN_CONFIG;

  return MULTI_DOMAIN_CONFIG?.[hostname] ?? null;
}

export function calculateDisbursalAmount(
  insurancePremium: number,
  gst: number,
  processingFee: number,
  amount: number
): number {
  const insurancePremiumPlusGST =
    ((insurancePremium + (gst * insurancePremium) / 100.0) * 100) / 100;

  // GST on processing fee
  const gstAmount = (processingFee * gst) / 100;
  let processingFeePlusGST = processingFee + gstAmount;
  processingFeePlusGST = (processingFeePlusGST * 100) / 100;

  return amount - processingFeePlusGST - insurancePremiumPlusGST;
}

export function formatKey(key: string): string {
  const parts = key.split("_");

  const processedParts = parts.map((part: string) => {
    // Split camelCase or PascalCase words, ignoring numbers at the end
    const words = part.split(/(?<![A-Z0-9])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/);

    // Capitalize each word
    const capitalizedWords = words
      .map(
        (word: string) =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join(" ");

    // Handle the case for any number (0-9) at the end of the word
    return capitalizedWords.replace(
      /(\d+)(?=$|[^a-zA-Z0-9])/g,
      (match) => " " + match
    );
  });

  // Join the processed parts with a space
  return processedParts.join(" ");
}

export function flattenAndFormatKeys(
  nestedObj: Record<string, unknown>
): Record<string, unknown> {
  const flatObj: Record<string, unknown> = {};

  function recurse(obj: Record<string, unknown>) {
    for (const key in obj) {
      if (Object.hasOwn(obj, key)) {
        if (typeof obj[key] === "object" && obj[key] !== null) {
          recurse(obj[key] as Record<string, unknown>);
        } else {
          flatObj[formatKey(key)] = obj[key];
        }
      }
    }
  }

  recurse(nestedObj);
  return flatObj;
}

export const sliceObjects = (obj = {}, sliceKeyCount = 0) => {
  const keys = Object.keys(obj);
  const slicesLength = Math.round(keys.length / sliceKeyCount);
  const result = [];
  const entries = Object.entries(obj);

  for (let i = 0; i < slicesLength; i++) {
    result.push(entries.splice(0, sliceKeyCount));
  }

  return result.map((item) => Object.fromEntries(item));
};

/**
 * @description Get the current lender short code from lender ID present in user data
 * @returns The lender short code (example: "MFL_LAP", etc.) or null if not found
 */
export const getCurrentLender = () => {
  const { userData } = getLenderUserInfoFromLocalStorage();

  const lenderId = userData?.lenderID;

  const entry = Object.entries(LENDER_IDs).find(([, id]) => id === lenderId);

  return entry ? entry[0] : null; // example : returns "MFL_LAP", etc.
};

/**
 * @description Get the current org short code from organization ID present in user data
 * @returns The org short code (example: "MFL_LAP", etc.) or null if not found
 */
export const getCurrentOrganisation = () => {
  const userInfo = getUserInfoFromLocalStorage();

  const orgId = userInfo?.organizationID;

  const entry = Object.entries(ORG_IDS).find(([, id]) => id === orgId);

  return entry ? entry[0] : null;
};

export const showMyQueue = (organizationID: string) => {
  const MY_QUEUE_ORGS = new Set([
    ORG_IDS.MFL_LAP,
    ORG_IDS.MFL,
    ORG_IDS.PFL_EL,
    ORG_IDS.HFCL,
    ORG_IDS.TVSCS
  ]);

  return MY_QUEUE_ORGS.has(organizationID ?? "") || false;
};

export const getUserLenderID = (): string => {
  let user: any = window.localStorage.getItem("finbox-lender-dashboard-user");

  if (user) {
    try {
      const parsedUser = JSON.parse(user);

      return parsedUser?.userData?.lenderID || "";
    } catch (error) {
      console.error("Error parsing user data", error);
    }
  }
  return "";
};

export const isLenderAxis = (): boolean =>
  getUserLenderID() === LENDER_IDs.AXIS_NEW;

export const isMFLLAP = (): boolean => getUserLenderID() === LENDER_IDs.MFL_LAP;

export const isAlphanumericWithSpaces = (value: string): boolean => {
  return !/[^a-zA-Z0-9\s]/.test(value);
};

export const getDocumentID = (activeTab: any, buttonKey?: string) => {
  switch (activeTab?.id) {
    case "fi_verify":
      return DOCUMENT_IDS.FI_VERIFY;
    case "fcu_verify":
      return DOCUMENT_IDS.FCU_VERIFY;
    case "review":
      if (buttonKey === "FCU Approval") {
        return DOCUMENT_IDS.FCU_VERIFY;
      } else if (buttonKey === "FI Approval") {
        return DOCUMENT_IDS.FI_VERIFY;
      } else if (buttonKey === "Decision") {
        return DOCUMENT_IDS.DECISION;
      }
      return "";
    default:
      return "";
  }
};

export const getPageName = (activeTab: any) => {
  switch (activeTab?.id) {
    case "fi_verify":
      return "FI Verify Tab";
    case "fcu_verify":
      return "FCU Verify Tab";
    case "review":
      return "Review Tab";
    default:
      return "";
  }
};

export const getMediaType = (activeTab: any) => {
  switch (activeTab?.id) {
    case "fi_verify":
      return "FI_Verification";
    case "fcu_verify":
      return "FCU_Verification";
    case "review":
      return "Review";
    default:
      return "";
  }
};

export const handleCheckStatusResponse = (
  response: any,
  reload: () => void
) => {
  if (response?.status) {
    let errorMessage =
      (response as any)?.data?.message || "Status check successful";
    const responseArr = (response as any)?.data?.response;
    let type: "success" | "error" = "success";

    if (Array.isArray(responseArr)) {
      const failureStep = responseArr?.find(
        (item: any) => item.status === "FAILURE"
      );

      if (failureStep?.message) {
        errorMessage = failureStep?.message;
        type = "error";
      }
    } else console.log("No object found with Failure status", responseArr);

    notify({
      title: type === "error" ? "Error" : "Success",
      text: errorMessage,
      type: type
    });

    reload();
  } else {
    notify({
      title: "Error",
      text: response?.error || "Something went wrong",
      type: "error"
    });
  }
};

/**
 * Organizations for which the My Queue flow is enabled.
 * Add the org id here to switch that client's "Home" breadcrumb /
 * post-action redirection from the Leads page to My Queue.
 */
export const MY_QUEUE_ENABLED_ORG_IDS: string[] = [
  ORG_IDS.MFL_LAP,
  ORG_IDS.HFCL
];

/**
 * Tells whether My Queue is enabled for the given organization.
 * @param organizationID organization id of the logged in user / entity
 * @returns {boolean} true -> redirect to /my-queue, false -> /leads
 */
export const isMyQueueEnabledOrg = (organizationID?: string): boolean =>
  !!organizationID && MY_QUEUE_ENABLED_ORG_IDS.includes(organizationID);
